import type { SocialPostPreviewCardMetadata, SocialPostPreviewMedia } from '@wix/ai-scheduling-assistant-common-lib/entities';

const LABELS: SocialPostPreviewCardMetadata['labels'] = {
  badge: 'Preview',
  postsNow: 'Goes out as soon as you approve it',
  scheduled: 'Scheduled for',
  textOnly: 'Text only',
  seeMore: 'See more',
  seeLess: 'See less',
};

const unsplash = (id: string, width = 1080, height = 1080) =>
  `https://images.unsplash.com/photo-${id}?w=${width}&h=${height}&q=80&auto=format&fit=crop`;

const SQUARE: SocialPostPreviewMedia = { url: unsplash('1509440159596-0249088772ff'), kind: 'image' };
const PORTRAIT: SocialPostPreviewMedia = { url: unsplash('1555507036-ab1f4038808a', 1080, 1350), kind: 'image' };
const LANDSCAPE: SocialPostPreviewMedia = { url: unsplash('1517433670267-08bbd4be890f', 1080, 566), kind: 'image' };
const TALL: SocialPostPreviewMedia = { url: unsplash('1486427944299-d1955d23e34d', 1080, 1920), kind: 'image' };
const CAROUSEL: SocialPostPreviewMedia[] = ['1549931319-a545dcf3bc73', '1568254183919-78a4f43a2877', '1608198093002-ad4e005484ec'].map((id) => ({
  url: unsplash(id),
  kind: 'image',
}));
const PORTRAIT_VIDEO: SocialPostPreviewMedia = { url: 'https://assets.mixkit.co/videos/40246/40246-720.mp4', kind: 'video' };
const VIDEO: SocialPostPreviewMedia = { url: 'https://assets.mixkit.co/videos/23056/23056-720.mp4', kind: 'video' };

const ACCOUNT: Pick<SocialPostPreviewCardMetadata, 'network' | 'networkLabel' | 'accountName' | 'accountLabel' | 'accountPictureUrl' | 'labels'> = {
  network: 'instagram',
  networkLabel: 'Instagram',
  accountName: '@joesbakery',
  accountLabel: 'joe.baker@example.com',
  accountPictureUrl: unsplash('1556910103-1c02745aae4d', 160, 160),
  labels: LABELS,
};

const post = (id: string, card: Partial<SocialPostPreviewCardMetadata>): SocialPostPreviewCardMetadata => ({
  ...ACCOUNT,
  media: [],
  mediaCount: 0,
  postType: 'post',
  decisionId: `ig-${id}`,
  taskId: `ig-task-${id}`,
  ...card,
});

export const INSTAGRAM_SAMPLES: { id: string; title: string; card: SocialPostPreviewCardMetadata }[] = [
  {
    id: 'ig-square',
    title: 'Instagram: one square photo',
    card: post('square', {
      caption: 'Fresh sourdough every Friday. Come early, it sells out by noon! #sourdough #freshbread',
      media: [SQUARE],
      mediaCount: 1,
    }),
  },
  {
    id: 'ig-portrait',
    title: 'Instagram: one 4:5 photo, long caption with a mention and a URL',
    card: post('portrait', {
      caption:
        'Croissant lamination day. 27 layers, 3 folds, and a lot of patience from @chef.dana and the morning crew.\n\nWe bake 200 of these before sunrise, so pre-order at https://joesbakery.example/order to make sure yours is waiting.\n\n#croissant #bakerylife #viennoiserie',
      media: [PORTRAIT],
      mediaCount: 1,
    }),
  },
  {
    id: 'ig-landscape',
    title: 'Instagram: one landscape photo, scheduled',
    card: post('landscape', {
      caption: 'The new shop front is finally done.',
      media: [LANDSCAPE],
      mediaCount: 1,
      scheduledAt: '2026-10-30T07:30:00.000Z',
    }),
  },
  {
    id: 'ig-carousel',
    title: 'Instagram: carousel of three',
    card: post('carousel', {
      postType: 'carousel',
      caption: 'Swipe for this week’s specials 👉',
      media: CAROUSEL,
      mediaCount: 3,
      labels: { ...LABELS, postType: 'Carousel' },
    }),
  },
  {
    id: 'ig-carousel-video',
    title: 'Instagram: carousel with a video, two not shown, expires',
    card: post('carousel-video', {
      postType: 'carousel',
      caption: 'Behind the scenes of a Saturday bake.',
      media: [PORTRAIT, PORTRAIT_VIDEO, CAROUSEL[1]],
      mediaCount: 5,
      expiresWithinDay: true,
      labels: {
        ...LABELS,
        postType: 'Carousel',
        mediaNotShown: 'Photos or videos not shown here: 2',
        expires: 'Instagram keeps a prepared post for about a day. Approve before then.',
      },
    }),
  },
  {
    id: 'ig-reel',
    title: 'Instagram: reel',
    card: post('reel', {
      postType: 'reel',
      caption: 'Shaping 200 loaves before sunrise. Order at https://joesbakery.example #sourdough #bakerylife @chef.dana',
      media: [PORTRAIT_VIDEO],
      mediaCount: 1,
      labels: { ...LABELS, postType: 'Reel' },
    }),
  },
  {
    id: 'ig-video',
    title: 'Instagram: one landscape video (posts as a reel)',
    card: post('video', {
      postType: 'video',
      caption: 'Rope day.',
      media: [VIDEO],
      mediaCount: 1,
      labels: { ...LABELS, postType: 'Video' },
    }),
  },
  {
    id: 'ig-story',
    title: 'Instagram: story, one 9:16 photo',
    card: post('story', {
      postType: 'story',
      media: [TALL],
      mediaCount: 1,
      labels: { ...LABELS, postType: 'Story' },
    }),
  },
  {
    id: 'ig-story-3',
    title: 'Instagram: story, three items',
    card: post('story-3', {
      postType: 'story',
      media: [SQUARE, PORTRAIT_VIDEO, TALL],
      mediaCount: 3,
      labels: { ...LABELS, postType: 'Story' },
    }),
  },
  {
    id: 'ig-hebrew',
    title: 'Instagram: Hebrew caption, no handle known',
    card: post('hebrew', {
      accountName: undefined,
      accountPictureUrl: undefined,
      caption: 'לחם מחמצת טרי בכל יום שישי. בואו מוקדם! https://joesbakery.example #מחמצת #לחם',
      media: [CAROUSEL[2]],
      mediaCount: 1,
    }),
  },
  {
    id: 'ig-broken',
    title: 'Instagram: photo that fails to load',
    card: post('broken', {
      caption: 'Holiday hours: closed on the 25th.',
      media: [{ url: 'https://images.unsplash.com/photo-0000000000000-missing', kind: 'image' }],
      mediaCount: 1,
    }),
  },
  {
    id: 'ig-text',
    title: 'Instagram: caption only (generic card)',
    card: post('text', {
      caption: 'Open late tonight for the street fair.',
      mediaCount: 1,
      labels: { ...LABELS, mediaNotShown: 'Photos or videos not shown here: 1' },
    }),
  },
];
